import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { PERMISSIONS, useOptionalPermission } from '@/lib/permissions'
import { useTenant } from '@/providers/TenantProvider'

const KEY = 'pending-reviews'

/** The badge count's key: under the worklist's root, so one invalidation refreshes both. */
const countKey = (tenantId: string | null | undefined) => [KEY, 'ready-count', tenantId] as const

/**
 * The signed-in user's review worklist (z8nrz7dt41). Not fetched at all
 * without `submissions.review`: the server would refuse, and a user who can
 * never review has no list, not an empty one.
 *
 * Each load also sets the top bar's badge count from the list (its `ready`
 * rows, which is what the server counts), so a review the list has just
 * picked up is on the badge too, without waiting for the badge's own poll.
 */
export function usePendingReviews() {
  const { selectedTenantId: tenantId } = useTenant()
  const { can } = useOptionalPermission()
  const canReview = can(PERMISSIONS.submissionsReview)
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: [KEY, tenantId] as const,
    queryFn: async () => {
      const rows = await formsApi.pendingReviews()
      qc.setQueryData(countKey(tenantId), { ready: rows.filter((r) => r.ready).length })
      return rows
    },
    enabled: !!tenantId && canReview,
    // Someone else completing a form is what adds to this list, so poll gently
    // rather than only refreshing on this user's own actions.
    refetchInterval: 60_000,
  })
  return { ...query, canReview }
}

/**
 * How many reviews the signed-in user can sign now, for the top bar's badge on
 * every page (z8nrz7fxhr). The server counts the worklist's `ready` rows, so
 * the badge never disagrees with the list, without sending the list itself.
 * Same gate and poll as `usePendingReviews`.
 */
export function usePendingReviewCount() {
  const { selectedTenantId: tenantId } = useTenant()
  const { can } = useOptionalPermission()
  const canReview = can(PERMISSIONS.submissionsReview)
  const query = useQuery({
    queryKey: countKey(tenantId),
    queryFn: () => formsApi.pendingReviewCount(),
    enabled: !!tenantId && canReview,
    refetchInterval: 60_000,
  })
  return { ...query, canReview }
}

/**
 * Refresh every copy of the worklist after a review or a completion. The badge
 * count sits under the same root key, so it refreshes too.
 */
export function useInvalidatePendingReviews() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: [KEY] })
}
